import { modelShortLabel } from "./computeCompareScorecard";

function shortIdLabel(id) {
    if (!id) return "—";
    const s = String(id).replace(/^openrouter:/, "");
    const parts = s.split("/");
    return parts[parts.length - 1] || s;
}

function formatLatency(ms) {
    if (!Number.isFinite(ms)) return "—";
    return `${Math.round(ms)}ms`;
}

function formatCost(usd) {
    if (!Number.isFinite(usd)) return "—";
    return `US$ ${usd.toFixed(usd >= 0.01 ? 2 : 3)}`;
}

export function verdictLabel(verdict) {
    switch (verdict) {
        case "reprovado":
            return "Reprovado";
        case "apto_para_promocao":
            return "Apto para promoção";
        case "empate_com_baseline":
            return "Empate com baseline";
        default:
            return verdict ? String(verdict) : "—";
    }
}

function verdictBadgeClass(verdict) {
    switch (verdict) {
        case "reprovado":
            return "bg-red-900/60 text-red-100 border-red-700/50";
        case "apto_para_promocao":
            return "bg-green-900/60 text-green-100 border-green-700/50";
        case "empate_com_baseline":
            return "bg-amber-900/60 text-amber-100 border-amber-700/50";
        default:
            return "bg-gray-800 text-gray-100 border-gray-600";
    }
}

export function buildScorecardMarkdown(scorecard, models = []) {
    const baselineLabel = shortIdLabel(scorecard.baselineModelId);
    const lines = ["## Scorecard compare", `Baseline: ${baselineLabel}`];

    for (const row of scorecard.perModel || []) {
        const label = shortIdLabel(row.id);
        const stats = `${row.pass}/${row.total} · ${formatLatency(row.latencyMsMedian)} · ${formatCost(row.costUsdTotal)}`;
        lines.push(`- ${label}: ${stats}`);
        for (const f of row.failures || []) {
            lines.push(`  - ${f.turnId} (${f.caseLabel}): ${f.assertId} — ${f.reason}`);
        }
    }

    const summary = scorecard.verdictSummary || "";
    lines.push(`Veredito: ${scorecard.verdict} — ${summary}`);
    return lines.join("\n");
}

export default function CompareScorecardPanel({
    scorecard,
    models = [],
    onFocusTurn,
}) {
    if (!scorecard) return null;

    const baselineLabel = shortIdLabel(scorecard.baselineModelId);

    const handleCopy = async () => {
        const md = buildScorecardMarkdown(scorecard, models);
        try {
            await navigator.clipboard.writeText(md);
        } catch {
            /* clipboard indisponível em alguns contextos */
        }
    };

    return (
        <div
            className="mb-3 rounded border border-[#b47c06]/40 bg-gray-950/80 px-3 py-3"
            data-testid="compare-scorecard"
        >
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <h3 className="text-sm font-medium text-white">Scorecard</h3>
                <div className="flex items-center gap-2">
                    <span
                        data-testid="compare-scorecard-verdict"
                        className={`rounded border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide ${verdictBadgeClass(scorecard.verdict)}`}
                    >
                        {verdictLabel(scorecard.verdict)}
                    </span>
                    <button
                        type="button"
                        onClick={handleCopy}
                        className="rounded border border-gray-600 px-2 py-0.5 text-[11px] text-gray-200 hover:border-laranjinha"
                        data-testid="compare-scorecard-copy"
                    >
                        Copiar resumo
                    </button>
                </div>
            </div>

            <p className="text-xs text-gray-400 mb-2">
                Baseline: <span className="text-gray-200">{baselineLabel}</span>
            </p>

            {scorecard.verdictSummary ? (
                <p className="text-xs text-gray-300 mb-2">{scorecard.verdictSummary}</p>
            ) : null}

            <table className="w-full text-xs text-left border-collapse">
                <thead>
                    <tr className="text-gray-400 border-b border-gray-700">
                        <th className="py-1 pr-2">Modelo</th>
                        <th className="py-1 pr-2">Pass</th>
                        <th className="py-1 pr-2">Latência</th>
                        <th className="py-1">Custo</th>
                    </tr>
                </thead>
                <tbody>
                    {(scorecard.perModel || []).map((row) => {
                        const isBaseline = row.id === scorecard.baselineModelId;
                        const slot = models.find((m) => m.id === row.id)?.slot;
                        const label = slot != null
                            ? modelShortLabel(models, slot)
                            : shortIdLabel(row.id);
                        return (
                            <tr
                                key={row.id}
                                className={`border-b border-gray-800/60 ${isBaseline ? "text-amber-100/90" : "text-gray-200"}`}
                            >
                                <td className="py-1 pr-2">
                                    {label}
                                    {isBaseline ? (
                                        <span className="ml-1 text-[10px] text-amber-400/80">(baseline)</span>
                                    ) : null}
                                </td>
                                <td className="py-1 pr-2">{row.pass}/{row.total}</td>
                                <td className="py-1 pr-2">{formatLatency(row.latencyMsMedian)}</td>
                                <td className="py-1">{formatCost(row.costUsdTotal)}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {(scorecard.perModel || []).some((r) => (r.failures || []).length > 0) ? (
                <div className="mt-2">
                    <p className="text-xs font-medium text-red-200/90 mb-1">Falhas</p>
                    <ul className="text-xs text-red-100/80 space-y-0.5 list-none">
                        {(scorecard.perModel || []).flatMap((row) =>
                            (row.failures || []).map((f) => {
                                const slot = models.find((m) => m.id === row.id)?.slot;
                                const modelLabel = slot != null
                                    ? modelShortLabel(models, slot)
                                    : shortIdLabel(row.id);
                                return (
                                    <li key={`${row.id}-${f.turnId}-${f.assertId}`}>
                                        <button
                                            type="button"
                                            className="text-left hover:underline"
                                            onClick={() => onFocusTurn?.(f.turnId)}
                                            data-testid={`compare-scorecard-failure-${f.turnId}`}
                                        >
                                            <span className="text-red-300/70">{modelLabel}</span>
                                            {" · "}
                                            <span className="text-red-200/60">{f.turnId}</span>
                                            {" ("}
                                            {f.caseLabel}
                                            {"): "}
                                            <span className="text-red-200/70">{f.assertId}</span>
                                            {" — "}
                                            {f.reason}
                                        </button>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            ) : null}
        </div>
    );
}
